import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { coachService } from '../services/coachService';
import userManager from '../services/userManager';
import api from '../services/api';
import { 
  Heart, 
  TrendingUp, 
  Calendar, 
  Clock, 
  ChevronRight,
  Star,
  Crown,
  Lock,
  Brain,
  Sparkles
} from 'lucide-react';

/**
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║                    COACH V1 CANONIQUE — HEAREN                             ║
 * ║                                                                            ║
 * ║  Guidance and reflection space.                                           ║
 * ║  "Help me understand or be guided."                                       ║
 * ║                                                                            ║
 * ║  Structure: Assistant → Guide/Programs → Progress (secondary)             ║
 * ║  Style: Calm, reassuring, optional, non-intrusive                         ║
 * ║  DO NOT MODIFY WITHOUT EXPLICIT PRODUCT DECISION                          ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */

const Coach = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listeningStats, setListeningStats] = useState({ sessions: 0, minutes: 0, streak: 0 });
  
  // Daily Check-in state (Premium)
  const [checkinLoading, setCheckinLoading] = useState(true);
  const [hasCheckinToday, setHasCheckinToday] = useState(false);
  const [checkinValue, setCheckinValue] = useState(5);
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);
  const [checkinSuccess, setCheckinSuccess] = useState(false);
  
  // Subscription status - single source of truth
  const isPremium = user?.isPremium === true || user?.subscription === 'premium' || user?.role === 'admin';
  const isFreeUser = !isPremium;
  const lang = localStorage.getItem('hearen_lang') || 'fr';

  useEffect(() => {
    loadPlan();
    loadListeningStats();
    if (isPremium) {
      loadTodayCheckin();
    }
  }, [isPremium]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const data = await coachService.getPlan();
      setPlan(data);
    } catch (err) {
      console.log('Coach plan not available');
    } finally {
      setLoading(false);
    }
  };

  const loadTodayCheckin = async () => {
    try {
      setCheckinLoading(true);
      
      // ⭐ V3 iOS FIX: Use coachService instead of direct fetch
      const data = await coachService.getTodayCheckin();
      setHasCheckinToday(data.has_checkin_today);
      if (data.checkin) {
        setCheckinValue(data.checkin.impact_score);
      }
    } catch (err) {
      console.error('[Coach] Error loading today checkin:', err);
    } finally {
      setCheckinLoading(false);
    }
  };

  const submitCheckin = async () => {
    try {
      setCheckinSubmitting(true);
      
      // ⭐ V3 iOS FIX: Use coachService instead of direct fetch
      await coachService.submitCheckin(checkinValue);
      setHasCheckinToday(true);
      setCheckinSuccess(true);
      // Haptic feedback on iOS if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Hide success message after 3 seconds
      setTimeout(() => setCheckinSuccess(false), 3000);
    } catch (err) {
      console.error('[Coach] Error submitting checkin:', err);
    } finally {
      setCheckinSubmitting(false);
    }
  };

  const loadListeningStats = async () => {
    const userId = userManager.getUserId();
    if (!userId) {
      console.log('[Coach] No user ID found, skipping stats load');
      return;
    }
    
    try {
      console.log('[Coach] Loading therapy sessions for user:', userId);
      const sessions = await api.getTherapySessions(userId);
      console.log('[Coach] Received sessions:', sessions?.length || 0, sessions);
      
      if (sessions && Array.isArray(sessions) && sessions.length > 0) {
        // Calculate total minutes from duration (seconds)
        const totalSeconds = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
        const totalMinutes = Math.round(totalSeconds / 60);
        
        // Calculate streak - consecutive days with sessions
        const sessionDates = [...new Set(sessions.map(s => {
          const d = new Date(s.date);
          return d.toISOString().split('T')[0];
        }))].sort().reverse();
        
        let streak = 0;
        const today = new Date().toISOString().split('T')[0];
        let checkDate = new Date();
        
        // Check consecutive days
        for (let i = 0; i < 30; i++) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (sessionDates.includes(dateStr)) {
            streak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else if (dateStr === today) {
            // Skip today if no session yet (allow checking yesterday)
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            break;
          }
        }
        
        console.log('[Coach] Calculated stats:', { sessions: sessions.length, minutes: totalMinutes, streak });
        setListeningStats({
          sessions: sessions.length,
          minutes: totalMinutes,
          streak: streak
        });
      }
    } catch (err) {
      console.error('[Coach] Error loading listening stats:', err);
    }
  };

  // Simple progress stats - use real listening stats when available
  const stats = listeningStats.sessions > 0 ? listeningStats : (plan?.progress ? {
    sessions: plan.progress.sessions_done || 0,
    minutes: Math.round((plan.progress.mins_done || 0) / 60),
    streak: plan.progress.streak_days || 0,
  } : { sessions: 0, minutes: 0, streak: 0 });

  // Get impact label based on score
  const getImpactLabel = (score) => {
    if (lang === 'en') {
      if (score === 0) return 'No impact';
      if (score <= 2) return 'Very mild';
      if (score <= 4) return 'Mild';
      if (score <= 6) return 'Moderate';
      if (score <= 8) return 'Significant';
      return 'Very bothersome';
    } else {
      if (score === 0) return 'Aucun impact';
      if (score <= 2) return 'Très léger';
      if (score <= 4) return 'Léger';
      if (score <= 6) return 'Modéré';
      if (score <= 8) return 'Important';
      return 'Très gênant';
    }
  };

  return (
    <div 
      className="min-h-screen pb-32 relative overflow-hidden"
      style={{ background: '#0a0a12' }}
    >
      {/* Subtle cosmic background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(88, 28, 135, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(59, 72, 238, 0.08) 0%, transparent 50%)
          `,
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-lg mx-auto px-5 pt-16">
        
        {/* Header - Calm and centered */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-white/10 mb-4">
            <Heart className="w-7 h-7 text-violet-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">
            {lang === 'en' ? 'Your Coach' : 'Votre Coach'}
          </h1>
          <p className="text-white/60 text-sm">
            {lang === 'en' 
              ? 'Guidance and support for your journey' 
              : 'Accompagnement et soutien pour votre parcours'}
          </p>
        </div>

        {/* DAILY CHECK-IN CARD (Premium Only) */}
        {isPremium && !checkinLoading && !hasCheckinToday && !checkinSuccess && (
          <section className="mb-6">
            <div 
              className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(88, 28, 135, 0.15) 0%, rgba(59, 72, 238, 0.1) 100%)'
              }}
            >
              {/* Subtle glow */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="relative">
                <h3 className="text-white font-semibold text-base mb-1">
                  {lang === 'en' 
                    ? 'How are you experiencing your tinnitus today?' 
                    : 'Comment vivez-vous vos acouphènes aujourd\'hui ?'}
                </h3>
                <p className="text-white/50 text-sm mb-5">
                  {lang === 'en'
                    ? 'Trust your impression of the moment — the trend matters more than the details.'
                    : 'Fiez-vous à votre impression du moment — la tendance compte plus que le détail.'}
                </p>
                
                {/* Slider with value display */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white/60 text-xs">
                      {lang === 'en' ? 'No impact' : 'Aucun impact'}
                    </span>
                    <span className="text-white font-bold text-lg">
                      {checkinValue.toFixed(0)}
                    </span>
                    <span className="text-white/60 text-xs">
                      {lang === 'en' ? 'Very bothersome' : 'Très gênant'}
                    </span>
                  </div>
                  
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={checkinValue}
                    onChange={(e) => setCheckinValue(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:w-5
                      [&::-webkit-slider-thumb]:h-5
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:bg-violet-400
                      [&::-webkit-slider-thumb]:border-2
                      [&::-webkit-slider-thumb]:border-white
                      [&::-webkit-slider-thumb]:shadow-lg
                      [&::-webkit-slider-thumb]:cursor-pointer
                      [&::-webkit-slider-thumb]:transition-transform
                      [&::-webkit-slider-thumb]:hover:scale-110
                      [&::-moz-range-thumb]:w-5
                      [&::-moz-range-thumb]:h-5
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:bg-violet-400
                      [&::-moz-range-thumb]:border-2
                      [&::-moz-range-thumb]:border-white
                      [&::-moz-range-thumb]:cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, rgba(139, 92, 246, 0.6) 0%, rgba(139, 92, 246, 0.6) ${checkinValue * 10}%, rgba(255,255,255,0.1) ${checkinValue * 10}%, rgba(255,255,255,0.1) 100%)`
                    }}
                  />
                  
                  <div className="text-center mt-2">
                    <span className="text-violet-300 text-sm font-medium">
                      {getImpactLabel(checkinValue)}
                    </span>
                  </div>
                </div>
                
                {/* Submit button */}
                <button
                  onClick={submitCheckin}
                  disabled={checkinSubmitting}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium text-sm
                    hover:from-violet-600 hover:to-purple-700 transition-all duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    shadow-lg shadow-violet-500/25"
                >
                  {checkinSubmitting 
                    ? (lang === 'en' ? 'Saving...' : 'Enregistrement...')
                    : (lang === 'en' ? 'Save my perception' : 'Enregistrer mon ressenti')}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Success message after check-in */}
        {isPremium && checkinSuccess && (
          <section className="mb-6">
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-4 text-center">
              <p className="text-emerald-400 text-sm font-medium">
                {lang === 'en' 
                  ? 'Perception recorded for today.' 
                  : 'Ressenti enregistré pour aujourd\'hui.'}
              </p>
            </div>
          </section>
        )}

        {/* SECTION 1: Assistant - Entry point to guidance */}
        <section className="mb-6">
          <button
            onClick={() => isFreeUser ? navigate('/premium') : navigate('/assistant')}
            className={`w-full p-5 rounded-2xl text-left transition-all duration-200 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/20 hover:border-violet-500/40 ${isFreeUser ? 'opacity-70' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-violet-300" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-base">
                    {lang === 'en' ? 'Ask Your Coach' : 'Parler au Coach'}
                  </h3>
                  <p className="text-white/50 text-sm mt-0.5">
                    {lang === 'en' ? 'Get personalized guidance' : 'Obtenir des conseils personnalisés'}
                  </p>
                </div>
              </div>
              {isFreeUser ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-amber-300/80 font-medium">Premium</span>
                  <Lock className="w-4 h-4 text-amber-300/80" />
                </div>
              ) : (
                <ChevronRight className="w-5 h-5 text-white/40" />
              )}
            </div>
          </button>
        </section>

        {/* SECTION 2: Guide / Programs - Non-medical, structured */}
        <section className="mb-6">
          <h2 className="text-white/70 text-xs font-medium uppercase tracking-wider mb-3 px-1">
            {lang === 'en' ? 'Explore' : 'Explorer'}
          </h2>
          
          <div className="space-y-3">
            {/* Breathing Program - Route based on subscription */}
            <button
              onClick={() => navigate('/breathing-premium')}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/8 hover:border-white/20 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <span className="text-lg">🌬️</span>
                  </div>
                  <div>
                    <h3 className="text-white font-medium text-sm">
                      {lang === 'en' ? 'Breathing Exercises' : 'Exercices de respiration'}
                    </h3>
                    <p className="text-white/50 text-xs mt-0.5">
                      {lang === 'en' ? 'Calm your mind' : 'Apaiser l\'esprit'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </div>
            </button>

            {/* Guide / Methodology */}
            <button
              onClick={() => navigate('/methodology')}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/8 hover:border-white/20 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <span className="text-lg">📚</span>
                  </div>
                  <div>
                    <h3 className="text-white font-medium text-sm">
                      {lang === 'en' ? 'Methodology' : 'Méthodologie'}
                    </h3>
                    <p className="text-white/50 text-xs mt-0.5">
                      {lang === 'en' ? 'How it works' : 'Comment ça fonctionne'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </div>
            </button>

            {/* Tests */}
            <button
              onClick={() => navigate('/tests')}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/8 hover:border-white/20 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <span className="text-lg">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-white font-medium text-sm">
                      {lang === 'en' ? 'Hearing Tests' : 'Tests auditifs'}
                    </h3>
                    <p className="text-white/50 text-xs mt-0.5">
                      {lang === 'en' ? 'Check your profile' : 'Vérifier votre profil'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </div>
            </button>
          </div>
        </section>

        {/* SECTION 3: Progress / History - Secondary, quiet */}
        <section className="mb-6">
          <h2 className="text-white/70 text-xs font-medium uppercase tracking-wider mb-3 px-1">
            {lang === 'en' ? 'Your Progress' : 'Votre progression'}
          </h2>
          
          {/* Simple stats - quiet, non-gamified */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-2xl font-semibold text-white">{stats.sessions}</div>
              <div className="text-white/50 text-xs mt-1">
                {lang === 'en' ? 'Sessions' : 'Séances'}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-2xl font-semibold text-white">{stats.minutes}</div>
              <div className="text-white/50 text-xs mt-1">
                {lang === 'en' ? 'Minutes' : 'Minutes'}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
              <div className="text-2xl font-semibold text-white">{stats.streak}</div>
              <div className="text-white/50 text-xs mt-1">
                {lang === 'en' ? 'Day streak' : 'Jours'}
              </div>
            </div>
          </div>

          {/* View history link - Premium only */}
          {!isFreeUser && (
            <button
              onClick={() => navigate('/progress')}
              className="w-full p-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 text-center hover:from-blue-500/30 hover:to-cyan-500/30 hover:border-blue-400/50 transition-all"
            >
              <span className="text-blue-300 font-medium text-sm flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 8v4l3 3" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
                {lang === 'en' ? 'View full history' : 'Voir l\'historique complet'}
              </span>
            </button>
          )}
          {isFreeUser && (
            <button
              onClick={() => navigate('/premium')}
              className="w-full p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-400/15 text-center opacity-70 transition-all"
            >
              <span className="text-blue-300/70 font-medium text-sm flex items-center justify-center gap-2">
                <Lock className="w-4 h-4 text-amber-300/80" />
                {lang === 'en' ? 'History & Graphs' : 'Historique & Graphes'}
                <span className="text-xs text-amber-300/80 font-medium ml-1">Premium</span>
              </span>
            </button>
          )}
        </section>

        {/* Premium upsell - soft, non-aggressive */}
        {isFreeUser && (
          <section className="mb-6">
            <button
              onClick={() => navigate('/premium')}
              className="w-full p-5 rounded-2xl bg-gradient-to-br from-violet-500/10 to-purple-600/10 border border-violet-500/20 text-left hover:border-violet-500/30 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-violet-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium text-base">
                    {lang === 'en' ? 'Unlock Premium' : 'Passer à Premium'}
                  </h3>
                  <p className="text-white/50 text-sm mt-0.5">
                    {lang === 'en' 
                      ? 'AI guidance, advanced insights, and more' 
                      : 'Coach IA, analyses avancées, et plus'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-violet-400/60" />
              </div>
            </button>
          </section>
        )}

        {/* Disclaimer - calm, informative */}
        <div className="text-center px-4">
          <p className="text-white/30 text-xs">
            {lang === 'en' 
              ? 'Wellness tool. Does not replace medical advice.' 
              : 'Outil de bien-être. Ne remplace pas un avis médical.'}
          </p>
        </div>

      </div>
    </div>
  );
};

export default Coach;
